import { useRef, RefObject } from "react"
import { FormikProps } from "formik"
import { enqueueSnackbar } from "notistack"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { createServiceCenterMessage, ServiceCenterMessagesQuery } from "../serviceCenterMessagesApi"
import { selectUserId, selectServiceCenterId } from "../../../../login/loginSelectors"
import { useAppSelector } from "../../../../store/storeHooks"
import { UserType } from "../../../../login/loginTypes"
import {
    CreateEditMessageFormValues,
    MessageAdditionalDataRecipientsType,
    MessageItem,
    ServiceCenterMessageAdditionalData,
} from "../serviceCenterMessagesTypes"

type UseCreateServiceCenterMessageFlowResult = {
    formRef: RefObject<FormikProps<CreateEditMessageFormValues>>
    handleSaveClick: () => void
    handleSubmit: (data: MessageItem) => void
}

export function useCreateServiceCenterMessageFlow(
    onAfterSuccess?: () => void,
): UseCreateServiceCenterMessageFlowResult {
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const userId = useAppSelector(selectUserId)

    const formRef = useRef<FormikProps<CreateEditMessageFormValues>>(null)

    const messageAdditionalData: ServiceCenterMessageAdditionalData = {
        sender: {
            type: UserType.SERVICE_CENTER,
            serviceCenter: {
                id: serviceCenterId || 0,
            },
            user: {
                id: userId || 0,
            },
        },
        recipients: [
            {
                type: MessageAdditionalDataRecipientsType.SERVICE_CENTER_USERS,
                userTypes: [UserType.USER],
            },
        ],
    }

    const { mutateAsync: callCreateMessage } = useApiMutation(createServiceCenterMessage, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ServiceCenterMessagesQuery.LIST] })
            onAfterSuccess?.()

            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    const handleSubmit = (data: MessageItem) => {
        void callCreateMessage({
            message: data,
            messageAdditionalData,
        })
    }

    return {
        formRef,
        handleSaveClick,
        handleSubmit,
    }
}
